import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fetchGallerySettings, mergeGallery, type GalleryItem } from "@/lib/gallery";

export const Route = createFileRoute("/tools/gallery")({
  head: () => ({ meta: [{ title: "Image SEO & Gallery | Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: GalleryAdmin,
});

type Row = GalleryItem & { dirty?: boolean };

function GalleryAdmin() {
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState<"all" | "shown" | "hidden">("all");

  const load = async () => setRows(mergeGallery(await fetchGallerySettings()));
  useEffect(() => { load(); }, []);

  const update = (key: string, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch, dirty: true } : r)));

  const save = async (r: Row, override?: Partial<Row>) => {
    const x = { ...r, ...override };
    const { error } = await supabase.from("gallery_images" as never).upsert(
      {
        image_key: x.key,
        alt_text: x.alt.trim() || null,
        title: x.displayTitle.trim() || null,
        caption: x.displayCaption.trim() || null,
        include_in_gallery: x.include,
        sort_order: x.sort,
        updated_at: new Date().toISOString(),
      } as never,
      { onConflict: "image_key" },
    );
    if (error) return toast.error(`Couldn't save: ${error.message}`);
    setRows((rs) => rs.map((y) => (y.key === x.key ? { ...x, dirty: false } : y)));
    toast.success("Image saved");
  };

  const shown = rows.filter((r) => r.include).length;
  const list = rows.filter((r) => filter === "all" || (filter === "shown" ? r.include : !r.include));

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground">Image SEO & Gallery</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Edit the alt text, title, and caption for every website image, and choose which ones appear on the{" "}
            <Link to="/gallery" className="text-brand-text underline">Gallery page</Link>. {shown} of {rows.length} shown.
          </p>
        </div>
        <div className="flex gap-2">
          {(["all", "shown", "hidden"] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`rounded-full border px-3 py-1 text-xs capitalize ${filter === f ? "bg-foreground text-background" : "border-border hover:bg-accent"}`}>{f}</button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {list.map((r) => (
          <div key={r.key} className="grid gap-4 rounded-lg border border-border bg-card p-4 md:grid-cols-[200px_1fr]">
            <div>
              <img src={r.loc} alt={r.alt} className={`aspect-video w-full rounded object-cover ${r.include ? "" : "opacity-40"}`} />
              <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={r.include} onChange={(e) => save(r, { include: e.target.checked })} />
                Show in gallery
              </label>
              <div className="mt-2 text-[11px] text-muted-foreground break-all">{r.key}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">Used on: {r.pages.slice(0, 3).join(", ")}{r.pages.length > 3 ? ` +${r.pages.length - 3}` : ""}</div>
            </div>
            <div className="grid gap-3">
              <label className="block">
                <div className="mb-1 text-xs font-medium text-muted-foreground">Alt text (describes the image for Google & screen readers)</div>
                <Input value={r.alt} maxLength={160} onChange={(e) => update(r.key, { alt: e.target.value })} />
              </label>
              <label className="block">
                <div className="mb-1 text-xs font-medium text-muted-foreground">Title</div>
                <Input value={r.displayTitle} maxLength={120} onChange={(e) => update(r.key, { displayTitle: e.target.value })} />
              </label>
              <label className="block">
                <div className="mb-1 text-xs font-medium text-muted-foreground">Caption</div>
                <Textarea rows={2} value={r.displayCaption} maxLength={300} onChange={(e) => update(r.key, { displayCaption: e.target.value })} />
              </label>
              <div className="flex items-end gap-3">
                <label className="block w-28">
                  <div className="mb-1 text-xs font-medium text-muted-foreground">Order</div>
                  <Input type="number" value={r.sort} onChange={(e) => update(r.key, { sort: Number(e.target.value) || 0 })} />
                </label>
                <Button onClick={() => save(r)} disabled={!r.dirty}>Save</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
